import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Award, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";

const EMPTY_FORM = {
  title: "",
  category: "Innovation",
  summary: "",
  details: "",
};

const CATEGORIES = ["Innovation", "Improvement", "Achievement"];

const categoryBadgeClass = (category) => {
  switch (category) {
    case "Achievement":
      return "border-amber-400/40 bg-amber-500/10 text-amber-700 dark:text-amber-300";
    case "Improvement":
      return "border-emerald-400/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
    default:
      return "border-violet-400/40 bg-violet-500/10 text-violet-700 dark:text-violet-300";
  }
};

export default function HostelInnovation() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const load = async () => {
    try {
      const response = await api.get("/hostel-innovation");
      setItems(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load hostel updates");
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      category: item.category || "Innovation",
      summary: item.summary || "",
      details: item.details || "",
    });
    setDialogOpen(true);
  };

  const saveItem = async () => {
    if (!form.title.trim() || !form.summary.trim() || !form.details.trim()) {
      return toast.error("Title, summary and details are required");
    }

    try {
      if (editingId) {
        await api.put(`/hostel-innovation/${editingId}`, form);
        toast.success("Entry updated");
      } else {
        await api.post("/hostel-innovation", form);
        toast.success("Entry added");
      }
      setDialogOpen(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save entry");
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete “${item.title}”?`)) return;
    try {
      await api.delete(`/hostel-innovation/${item.id}`);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      toast.success("Entry removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete entry");
    }
  };

  const canManage = (item) => user?.role === "admin" || user?.id === item.author_id;
  const filteredItems = selectedCategory === "All"
    ? items
    : items.filter((item) => (item.category || "Innovation") === selectedCategory);

  return (
    <div className="space-y-7" data-testid="hostel-innovation-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
            <Sparkles className="h-4 w-4" /> Hostel Life
          </p>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold">Hostel Innovation & Achievement</h1>
        </div>
        <Button onClick={openCreate} data-testid="hostel-innovation-new-btn">
          <Plus className="mr-2 h-4 w-4" /> New entry
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 border-t-4 border-sky-500 pt-4">
        {['All', ...CATEGORIES].map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setSelectedCategory(option)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
              selectedCategory === option
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-muted-foreground hover:text-foreground"
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      {filteredItems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          {items.length === 0 ? "No hostel innovations or achievements recorded yet." : "No entries match this category."}
        </div>
      ) : (
        <div className="space-y-4 border-t-4 border-emerald-500 pt-4">
          {filteredItems.map((item) => (
            <article key={item.id} className="rounded-xl border border-border bg-card p-5 shadow-sm" data-testid={`hostel-innovation-item-${item.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${categoryBadgeClass(item.category || "Innovation")}`}>
                      <Award className="h-3.5 w-3.5" /> {item.category || "Innovation"}
                    </span>
                    <span className="text-xs text-muted-foreground">Recorded by {item.author_name}</span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-extrabold">{item.title}</h2>
                  <p className="mt-2 text-sm font-medium text-muted-foreground">{item.summary}</p>
                </div>

                {canManage(item) && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} aria-label={`Edit ${item.title}`} title="Edit entry">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteItem(item)} aria-label={`Delete ${item.title}`} title="Delete entry">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-foreground/90">{item.details}</p>
            </article>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit hostel innovation" : "Add hostel innovation"}</DialogTitle>
            <DialogDescription>Document changes, improvements, innovations, and achievements related to the hostel.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} data-testid="hostel-innovation-title-input" />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value })}
                data-testid="hostel-innovation-category-input"
              >
                {CATEGORIES.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Summary</Label>
              <Input value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} data-testid="hostel-innovation-summary-input" />
            </div>

            <div className="space-y-2">
              <Label>Details</Label>
              <Textarea rows={5} value={form.details} onChange={(event) => setForm({ ...form, details: event.target.value })} data-testid="hostel-innovation-details-input" />
            </div>
          </div>

          <DialogFooter>
            <Button onClick={saveItem} data-testid="hostel-innovation-submit-btn">
              {editingId ? "Save changes" : "Add entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
